import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, onSnapshot, setDoc, deleteDoc, serverTimestamp, type Timestamp } from 'firebase/firestore';
import {
  CheckCircle2,
  XCircle,
  Circle,
  Download,
  FileText,
  Printer,
  RotateCcw,
  Settings,
  ListChecks,
  ClipboardList,
  Info,
} from 'lucide-react';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import MainLayout from '../../components/layout/MainLayout';
import { Button } from '../../components/ui/Button';
import { getEscenario } from '../../data/escenariosPruebas';
import { descargarMarkdown, imprimirComoPdf } from '../../utils/pruebasExport';
import type { EstadoResultado } from '../../types/pruebas';
import styles from './PruebaDetallePage.module.css';

interface ResultadoDoc {
  estado: EstadoResultado;
  causa: string;
  actualizadoEn: Timestamp | null;
  actualizadoPorNombre?: string;
}

export default function PruebaDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();

  const escenario = id ? getEscenario(id) : undefined;

  const [resultado, setResultado] = useState<ResultadoDoc | null>(null);
  const [estadoElegido, setEstadoElegido] = useState<EstadoResultado | null>(null);
  const [causa, setCausa] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!id) return;
    const unsub = onSnapshot(
      doc(db, 'pruebas_resultados', id),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as ResultadoDoc;
          setResultado(data);
          setEstadoElegido(data.estado);
          setCausa(data.causa || '');
        } else {
          setResultado(null);
          setEstadoElegido(null);
          setCausa('');
        }
        setCargando(false);
      },
      () => setCargando(false),
    );
    return () => unsub();
  }, [id]);

  const guardarResultado = useCallback(async () => {
    if (!id || !estadoElegido) return;
    if (estadoElegido === 'fail' && causa.trim().length < 3) {
      addToast('error', 'Describa la causa del fallo antes de guardar.');
      return;
    }
    setGuardando(true);
    try {
      // Sobrescribe COMPLETO el documento — no se conserva historial de corridas
      // anteriores, tal como se requirió (cada corrida limpia el resultado previo).
      await setDoc(doc(db, 'pruebas_resultados', id), {
        estado: estadoElegido,
        causa: causa.trim(),
        actualizadoEn: serverTimestamp(),
        actualizadoPor: user?.uid || '',
        actualizadoPorNombre: user?.displayName || user?.email || '',
      });
      addToast('success', 'Resultado guardado.');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'No se pudo guardar el resultado.');
    } finally {
      setGuardando(false);
    }
  }, [id, estadoElegido, causa, user, addToast]);

  const reiniciarResultado = useCallback(async () => {
    if (!id) return;
    setGuardando(true);
    try {
      await deleteDoc(doc(db, 'pruebas_resultados', id));
      setEstadoElegido(null);
      setCausa('');
      addToast('success', 'Resultado reiniciado. Listo para una nueva corrida.');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'No se pudo reiniciar.');
    } finally {
      setGuardando(false);
    }
  }, [id, addToast]);

  if (!escenario) {
    return (
      <MainLayout pageTitle="Escenario no encontrado" onBack={() => navigate('/admin/pruebas')}>
        <p>No se encontró el escenario «{id}».</p>
      </MainLayout>
    );
  }

  const resultadoParaExport = {
    estado: resultado?.estado ?? null,
    causa: resultado?.causa ?? '',
    actualizadoEn: resultado?.actualizadoEn?.toDate?.() ?? null,
    actualizadoPorNombre: resultado?.actualizadoPorNombre,
  };

  const hayCambiosSinGuardar =
    !cargando &&
    (estadoElegido !== (resultado?.estado ?? null) || causa.trim() !== (resultado?.causa ?? ''));

  return (
    <MainLayout
      pageTitle={`${escenario.id} · ${escenario.titulo}`}
      pageSubtitle={escenario.area}
      onBack={() => navigate('/admin/pruebas')}
    >
      <div className={styles.page}>
        {/* ── Objetivo ── */}
        <section className={styles.card}>
          <header className={styles.cardHead}>
            <Info size={16} aria-hidden="true" />
            <h2 className={styles.cardTitle}>Objetivo</h2>
          </header>
          <p className={styles.cardBody}>{escenario.objetivo}</p>
        </section>

        {/* ── Configuración inicial ── */}
        <section className={styles.card}>
          <header className={styles.cardHead}>
            <Settings size={16} aria-hidden="true" />
            <h2 className={styles.cardTitle}>Configuración inicial</h2>
          </header>
          <ul className={styles.list}>
            {escenario.configuracionInicial.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </section>

        {/* ── Cambios de configuración ── */}
        {escenario.cambiosConfiguracion && escenario.cambiosConfiguracion.length > 0 && (
          <section className={styles.card}>
            <header className={styles.cardHead}>
              <Settings size={16} aria-hidden="true" />
              <h2 className={styles.cardTitle}>Cambios de configuración durante la prueba</h2>
            </header>
            <ul className={styles.list}>
              {escenario.cambiosConfiguracion.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </section>
        )}

        {/* ── Paso a paso ── */}
        <section className={styles.card}>
          <header className={styles.cardHead}>
            <ClipboardList size={16} aria-hidden="true" />
            <h2 className={styles.cardTitle}>Paso a paso</h2>
          </header>
          <ol className={styles.pasoList}>
            {escenario.pasos.map((p, i) => (
              <li key={i} className={styles.pasoItem}>
                <span className={styles.pasoNum}>{i + 1}</span>
                <div>
                  <span className={styles.pasoActor}>{p.actor}</span>
                  <span className={styles.pasoAccion}>{p.accion}</span>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* ── Checkpoints ── */}
        <section className={styles.card}>
          <header className={styles.cardHead}>
            <ListChecks size={16} aria-hidden="true" />
            <h2 className={styles.cardTitle}>Checkpoints — qué debe cumplirse</h2>
          </header>
          <ul className={styles.list}>
            {escenario.checkpoints.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </section>

        {escenario.notas && (
          <div className={styles.notasBanner}>
            <Info size={16} aria-hidden="true" />
            <div>{escenario.notas}</div>
          </div>
        )}

        {/* ── Resultado ── */}
        <section className={styles.card}>
          <header className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Resultado de esta corrida</h2>
            {resultado && (
              <span className={styles.resultadoMeta}>
                Actualizado {resultado.actualizadoEn?.toDate?.().toLocaleString('es-PA') ?? '—'}
                {resultado.actualizadoPorNombre ? ` por ${resultado.actualizadoPorNombre}` : ''}
              </span>
            )}
          </header>
          <div className={styles.cardBody}>
            <div className={styles.estadoToggle} role="radiogroup" aria-label="Resultado de la prueba">
              <button
                type="button"
                role="radio"
                aria-checked={estadoElegido === 'pass'}
                className={`${styles.estadoBtn} ${styles.estadoBtnPass} ${estadoElegido === 'pass' ? styles.estadoBtnActive : ''}`}
                onClick={() => setEstadoElegido('pass')}
              >
                <CheckCircle2 size={16} aria-hidden="true" /> Pasó
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={estadoElegido === 'fail'}
                className={`${styles.estadoBtn} ${styles.estadoBtnFail} ${estadoElegido === 'fail' ? styles.estadoBtnActive : ''}`}
                onClick={() => setEstadoElegido('fail')}
              >
                <XCircle size={16} aria-hidden="true" /> Falló
              </button>
              {!estadoElegido && (
                <span className={styles.estadoNone}>
                  <Circle size={13} aria-hidden="true" /> Sin correr
                </span>
              )}
            </div>

            <label className={styles.causaLabel} htmlFor="causa-textarea">
              Causa / observaciones {estadoElegido === 'fail' && <span className={styles.required}>(requerido si falló)</span>}
            </label>
            <textarea
              id="causa-textarea"
              className={styles.causaTextarea}
              rows={4}
              value={causa}
              placeholder="Describa qué ocurrió, en qué paso, y cualquier detalle relevante para reproducir el problema…"
              onChange={(e) => setCausa(e.target.value)}
            />

            <div className={styles.resultadoActions}>
              <Button
                variant="primary"
                onClick={guardarResultado}
                disabled={!estadoElegido || guardando || !hayCambiosSinGuardar}
                loading={guardando}
              >
                Guardar resultado
              </Button>
              {resultado && (
                <Button variant="secondary" onClick={reiniciarResultado} disabled={guardando}>
                  <RotateCcw size={14} /> Reiniciar (nueva corrida)
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* ── Descargas ── */}
        <section className={styles.downloadRow}>
          <Button variant="secondary" onClick={() => descargarMarkdown(escenario, resultadoParaExport)}>
            <Download size={14} /> Descargar .md
          </Button>
          <Button variant="secondary" onClick={() => imprimirComoPdf(escenario, resultadoParaExport)}>
            <Printer size={14} /> Descargar PDF
          </Button>
          <span className={styles.downloadHint}>
            <FileText size={13} aria-hidden="true" /> El PDF se genera con el diálogo de impresión del navegador — elija «Guardar como PDF».
          </span>
        </section>
      </div>
    </MainLayout>
  );
}
